import type { PurchaseLineView } from '@obraplan/core/application/contracts/purchase-lines';
import { BusinessDate } from '@obraplan/core/domain/date/business-date';
import {
  classifyAlert,
  type AlertSummary,
  type MaterialAlertLevel,
} from '@obraplan/core/domain/schedule/material-alert';

/**
 * A vista de materiais como linha do tempo (redesenho de 21/10/2026).
 *
 * A pergunta da vista é "o que precisa chegar e quando", e por isso a lista se organiza pela
 * **semana de necessidade**, e não pelo pavimento: a semana que passou é a que venceu, a de
 * hoje é a que se compra agora. Tudo aqui é conta de exibição sobre as linhas já carregadas;
 * quem decide o que é vencido e o que está no horizonte é o domínio (`classifyAlert`) e o
 * resumo de avisos do servidor, que conhece o horizonte da obra (RN-066).
 */

/** A segunda-feira da semana de `date` (`AAAA-MM-DD`). Data inválida volta como está. */
export function weekStartOf(date: string): string {
  const parsed = BusinessDate.parse(date);
  if (!parsed.ok) return date;
  return parsed.value.subtractDays(parsed.value.weekday() - 1).toString();
}

/** As segundas-feiras de `from` a `to`, as semanas vazias incluídas: é a régua do ritmo. */
export function weeksBetween(from: string, to: string): readonly string[] {
  const first = BusinessDate.parse(weekStartOf(from));
  const last = BusinessDate.parse(weekStartOf(to));
  if (!first.ok || !last.ok) return [];
  const weeks: string[] = [];
  for (let cursor = first.value; !cursor.isAfter(last.value); cursor = cursor.addDays(7)) {
    weeks.push(cursor.toString());
  }
  return weeks;
}

export type WeekStatus = 'PAST' | 'CURRENT' | 'FUTURE';

/** A semana já passou inteira, é a de hoje, ou ainda vem. */
export function weekStatus(weekStart: string, today: string): WeekStatus {
  const start = BusinessDate.parse(weekStart);
  if (!start.ok) return 'FUTURE';
  const end = start.value.addDays(6).toString();
  if (end < today) return 'PAST';
  return weekStart <= today ? 'CURRENT' : 'FUTURE';
}

/** O último dia da semana que começa em `weekStart`. */
export function weekEndOf(weekStart: string): string {
  const start = BusinessDate.parse(weekStart);
  return start.ok ? start.value.addDays(6).toString() : weekStart;
}

export interface MaterialWeek {
  /** A segunda-feira. */
  readonly start: string;
  /** O domingo. */
  readonly end: string;
  readonly status: WeekStatus;
  readonly lines: readonly PurchaseLineView[];
}

/**
 * As linhas por semana de necessidade, da mais antiga à mais recente. Dentro da semana, pela
 * data, pelo pavimento (do nível mais baixo), pelo serviço e pelo material.
 */
export function groupByWeek(
  lines: readonly PurchaseLineView[],
  today: string,
): readonly MaterialWeek[] {
  const sorted = [...lines].sort(
    (a, b) =>
      a.requiredDate.localeCompare(b.requiredDate) ||
      a.floorLevel - b.floorLevel ||
      (a.serviceName ?? '').localeCompare(b.serviceName ?? '', 'pt-BR') ||
      a.materialName.localeCompare(b.materialName, 'pt-BR'),
  );
  const weeks = new Map<string, PurchaseLineView[]>();
  for (const line of sorted) {
    const start = weekStartOf(line.requiredDate);
    const lista = weeks.get(start) ?? [];
    lista.push(line);
    weeks.set(start, lista);
  }
  return [...weeks.entries()].map(([start, items]) => ({
    start,
    end: weekEndOf(start),
    status: weekStatus(start, today),
    lines: items,
  }));
}

/** O que o resumo da vista diz de cada linha. */
export interface MaterialDeadline {
  readonly level: MaterialAlertLevel;
  /** Dias corridos até a data de necessidade; negativo quando passou. */
  readonly daysUntil: number;
}

/**
 * Quem diz se a linha venceu ou está no horizonte: o resumo de avisos do servidor, que
 * aplica o horizonte da obra (RN-066); sem ele, a conta pelo horizonte padrão. Os dias até a
 * data saem sempre do calendário, com o hoje da tela.
 */
export function deadlineOf(
  alerts: AlertSummary | undefined,
  today: string,
): (line: Pick<PurchaseLineView, 'id' | 'requiredDate'>) => MaterialDeadline {
  const parsedToday = BusinessDate.parse(today);
  const levels = new Map(alerts?.alerts.map((alert) => [alert.entryId, alert.level]) ?? []);
  return (line) => {
    if (!parsedToday.ok) return { level: 'SCHEDULED', daysUntil: 0 };
    const base = classifyAlert(line.requiredDate, parsedToday.value);
    if (alerts === undefined) return base;
    return { level: levels.get(line.id) ?? 'SCHEDULED', daysUntil: base.daysUntil };
  };
}

export interface MaterialsSummary {
  readonly total: number;
  readonly overdue: number;
  readonly dueSoon: number;
  /** Com código provisório `ORC-…`: não vira requisição até o vínculo ao SKU (RN-347). */
  readonly provisional: number;
  /** Quantas vieram do quantitativo (RN-449). */
  readonly fromTakeoff: number;
  readonly first?: string;
  readonly last?: string;
}

/** O resumo da linha de cima: quantas, de quando a quando, quantas vencidas e no horizonte. */
export function summarizeMaterials(
  lines: readonly PurchaseLineView[],
  deadline: (line: Pick<PurchaseLineView, 'id' | 'requiredDate'>) => MaterialDeadline,
): MaterialsSummary {
  let overdue = 0;
  let dueSoon = 0;
  let provisional = 0;
  let fromTakeoff = 0;
  let first: string | undefined;
  let last: string | undefined;
  for (const line of lines) {
    const { level } = deadline(line);
    if (level === 'OVERDUE') overdue += 1;
    else if (level === 'DUE_SOON') dueSoon += 1;
    if (line.materialCodeProvisional) provisional += 1;
    if (line.origin === 'QUANTITATIVO') fromTakeoff += 1;
    if (first === undefined || line.requiredDate < first) first = line.requiredDate;
    if (last === undefined || line.requiredDate > last) last = line.requiredDate;
  }
  return {
    total: lines.length,
    overdue,
    dueSoon,
    provisional,
    fromTakeoff,
    ...(first === undefined ? {} : { first }),
    ...(last === undefined ? {} : { last }),
  };
}
